import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { productsPlaceholderSubcategoryList } from '@/api/generated/products-placeholder-subcategories/products-placeholder-subcategories';
import type { PlaceholderSubcategoryListItem } from '@/api/generated/schemas';
import { Badge } from '@/shared/ui';
import { col, filter, type ListPageConfig } from '@/shared/ui/list-page';
import { NavListPage } from '@/shared/navigation/react/NavListPage';
import { useNavigator } from '@/shared/navigation/react/useNavigator';
import { subcategoryDetailAgenda } from '../subcategoryDetail/subcategoryDetailAgenda';
import { subcategoryListAgenda } from './subcategoryListAgenda';
import { PLACEHOLDER_SUBCATEGORY_LIST_KEY } from '../internal/schemesQueryKeys';
import {
  placeholderStatusOptions,
  placeholderTypeOptions,
  statusTone,
  type EnumOption,
} from '../internal/placeholderEnums';
import { staticEnumOptions } from '../internal/staticEnumOptions';

/** Lokalizovaný popisek enum hodnoty z voleb (fallback = původní hodnota). */
function enumLabel<T extends string>(options: EnumOption<T>[], value: T): string {
  return options.find((option) => option.value === value)?.label ?? value;
}

function buildSubcategoryListConfig(
  openSubcategory: (id: string | undefined) => void,
  t: TFunction
): ListPageConfig<PlaceholderSubcategoryListItem> {
  const typeOptions = placeholderTypeOptions(t);
  const statusOptions = placeholderStatusOptions(t);

  return {
    key: PLACEHOLDER_SUBCATEGORY_LIST_KEY,
    i18n: { namespace: 'venua-planner' },
    api: { list: (request, signal) => productsPlaceholderSubcategoryList(request, signal) },
    rowKey: (item) => item.id,
    defaultSort: { column: 'name', desc: false },
    fulltext: { placeholder: (tt) => tt('schemes.subcategoryList.search') },
    onOpen: (item) => openSubcategory(item.id),
    create: {
      label: (tt) => tt('schemes.subcategoryList.new'),
      onClick: () => openSubcategory(undefined),
    },
    columns: (tt) => [
      col.strong<PlaceholderSubcategoryListItem>('name', {
        label: tt('schemes.subcategoryList.cols.name'),
        w: 240,
        frozen: true,
        link: false,
      }),
      col.mono<PlaceholderSubcategoryListItem>('code', {
        label: tt('schemes.subcategoryList.cols.code'),
        w: 120,
      }),
      col.custom<PlaceholderSubcategoryListItem>({
        key: 'type',
        label: tt('schemes.subcategoryList.cols.type'),
        w: 150,
        cell: (item) => <Badge tone="info">{enumLabel(typeOptions, item.type)}</Badge>,
      }),
      col.custom<PlaceholderSubcategoryListItem>({
        key: 'status',
        label: tt('schemes.subcategoryList.cols.status'),
        w: 120,
        cell: (item) => (
          <Badge tone={statusTone(item.status)}>{enumLabel(statusOptions, item.status)}</Badge>
        ),
      }),
    ],
    filterGroups: (tt) => [{ id: 'basic', label: tt('schemes.subcategoryList.groups.basic') }],
    filters: (tt) => [
      filter.text('name', {
        label: tt('schemes.subcategoryList.filters.name'),
        group: 'basic',
        freq: true,
      }),
      filter.enum('type', {
        label: tt('schemes.subcategoryList.filters.type'),
        group: 'basic',
        freq: true,
        options: staticEnumOptions(typeOptions),
      }),
      filter.enum('status', {
        label: tt('schemes.subcategoryList.filters.status'),
        group: 'basic',
        options: staticEnumOptions(statusOptions),
      }),
    ],
  };
}

/**
 * ListPage subkategorií placeholderů. Otevření řádku i „Nová subkategorie" míří na
 * `subcategoryDetailAgenda` (modální vrstva).
 */
export function SubcategoryListPage({ layerId }: { layerId: string }) {
  const { t } = useTranslation('venua-planner');
  const navigator = useNavigator();

  const openSubcategory = useCallback(
    (id: string | undefined) => {
      void navigator.openModal(subcategoryDetailAgenda, { subcategoryId: id });
    },
    [navigator]
  );

  const config = useMemo(
    () => buildSubcategoryListConfig(openSubcategory, t),
    [openSubcategory, t]
  );

  return <NavListPage agenda={subcategoryListAgenda} layerId={layerId} config={config} />;
}
